// Bilingual URL helpers. The site mirrors every public English route under
// `/fi/...`. Dynamic slugs (places, events, cities) stay identical between
// trees — only the section prefix is duplicated. Localised section slugs
// (e.g. /syo, /tee) are deliberately NOT used yet: keeping URLs aligned
// makes the mirror layer thin and the LanguageSwitcher trivial.

import type { LangCode } from "@/i18n/config";

export const SITE_ORIGIN = "https://whatfinland.com";

export type SiteLocale = "en" | "fi";

/** Resolve the active locale from the current pathname. */
export function localeFromPath(pathname: string): SiteLocale {
  return pathname === "/fi" || pathname.startsWith("/fi/") ? "fi" : "en";
}

/** Strip a leading `/fi` segment, returning the English path. */
export function toEnglishPath(pathname: string): string {
  if (pathname === "/fi") return "/";
  if (pathname.startsWith("/fi/")) return pathname.slice(3) || "/";
  return pathname;
}

/** Add a `/fi` prefix, returning the Finnish path. */
export function toFinnishPath(pathname: string): string {
  if (pathname === "/fi" || pathname.startsWith("/fi/")) return pathname;
  if (pathname === "/") return "/fi";
  return `/fi${pathname}`;
}

/** Switch the locale segment of a pathname to the target locale. */
export function pathForLocale(pathname: string, locale: SiteLocale): string {
  return locale === "fi" ? toFinnishPath(pathname) : toEnglishPath(pathname);
}

/**
 * Three `<link rel="alternate" hreflang>` entries for a given pathname.
 * English is the x-default. Pass into route `head().links`.
 */
export function buildHreflangLinks(pathname: string): Array<{ rel: string; hrefLang: string; href: string }> {
  const en = toEnglishPath(pathname);
  const fi = toFinnishPath(en);
  return [
    { rel: "alternate", hrefLang: "en", href: `${SITE_ORIGIN}${en}` },
    { rel: "alternate", hrefLang: "fi", href: `${SITE_ORIGIN}${fi}` },
    { rel: "alternate", hrefLang: "x-default", href: `${SITE_ORIGIN}${en}` },
  ];
}

/** Routes for which Finnish mirrors exist. Used by sitemap to gate alternates. */
const MIRRORED_PREFIXES: ReadonlyArray<string> = [
  "/",
  "/eat",
  "/do",
  "/happening",
  "/cities",
  "/place/",
  "/happening/",
  "/helsinki",
  "/turku",
  "/tampere",
  "/hanko",
  "/porvoo",
  "/national-parks",
  "/saunas",
  "/ski-resorts",
  "/restaurants",
  "/cafes",
  "/bars",
  "/pubs",
  "/menus",
];

export function hasMirror(path: string): boolean {
  if (path === "/") return true;
  // Skip the bare "/" entry — every path starts with it, which would wrongly
  // report a Finnish mirror for routes that don't have one (e.g. /map).
  return MIRRORED_PREFIXES.some((p) =>
    p === "/" ? false : p.endsWith("/") ? path.startsWith(p) : path === p || path.startsWith(`${p}/`),
  );
}

/** Narrow a UI language code to a site locale (only en/fi are URL-routed). */
export function asSiteLocale(lang: LangCode): SiteLocale {
  return lang === "fi" ? "fi" : "en";
}

/**
 * og:locale + og:locale:alternate pair for a page in the given locale.
 * Spread into route `head().meta` so crawlers see the language of THIS page
 * and know a sibling exists in the other language.
 */
export function buildLocaleMeta(locale: SiteLocale): Array<{ property: string; content: string }> {
  return locale === "fi"
    ? [
        { property: "og:locale", content: "fi_FI" },
        { property: "og:locale:alternate", content: "en_US" },
      ]
    : [
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "fi_FI" },
      ];
}
